/**
 * Utility to force clear all notification-related caches and storage
 * This addresses the persistent "15 notifications" bug by clearing all possible sources
 */

// Diagnostics only — this utility runs on every app start, so its progress
// chatter must not reach a borrower's production console (console-noise
// cleanup 2026-07-29). Failures still log below.
const devLog = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.log(...args);
};

export interface NotificationCacheClearResult {
  clearedLocalStorage: string[];
  clearedSessionStorage: string[];
  reactQueryCacheCleared: boolean;
}

/**
 * Force clear all notification-related caches and storage
 * Can be called from browser console: window.clearNotificationCache()
 */
export function clearNotificationCache(queryClient?: any): NotificationCacheClearResult {
  devLog('[clearNotificationCache] Starting comprehensive notification cache clear...');
  
  const result: NotificationCacheClearResult = {
    clearedLocalStorage: [],
    clearedSessionStorage: [],
    reactQueryCacheCleared: false,
  };

  // List of all possible notification storage keys
  const notificationKeys = [
    'path_mock_notifications',
    'path_notifications', 
    'notifications',
    'mock_notifications',
    'path_mock_notifications_backup',
    'notification_cache',
    'path_notification_cache',
    'tanstack_query_notification',
    'react_query_notification',
  ];

  // Clear localStorage
  if (typeof window !== 'undefined' && window.localStorage) {
    notificationKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        localStorage.removeItem(key);
        result.clearedLocalStorage.push(key);
        devLog(`[clearNotificationCache] Cleared localStorage key: ${key}`);
      }
    });
    
    // Also check for any keys that contain 'notification' in their name
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.toLowerCase().includes('notification')) {
        const value = localStorage.getItem(key);
        if (value !== null && !result.clearedLocalStorage.includes(key)) {
          localStorage.removeItem(key);
          result.clearedLocalStorage.push(key);
          devLog(`[clearNotificationCache] Cleared localStorage key (pattern match): ${key}`);
        }
      }
    }
  }

  // Clear sessionStorage
  if (typeof window !== 'undefined' && window.sessionStorage) {
    notificationKeys.forEach(key => {
      const value = sessionStorage.getItem(key);
      if (value !== null) {
        sessionStorage.removeItem(key);
        result.clearedSessionStorage.push(key);
        devLog(`[clearNotificationCache] Cleared sessionStorage key: ${key}`);
      }
    });
  }

  // Clear React Query cache if available
  if (queryClient) {
    try {
      queryClient.removeQueries({ queryKey: ['notifications'] });
      queryClient.removeQueries({ queryKey: ['notification'] }); 
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      result.reactQueryCacheCleared = true;
      devLog('[clearNotificationCache] Cleared React Query notification cache');
    } catch (error) {
      console.error('[clearNotificationCache] Error clearing React Query cache:', error);
    }
  }

  devLog('[clearNotificationCache] Complete. Results:', result);

  return result;
}

/**
 * Make function available in browser console for debugging
 */
if (typeof window !== 'undefined') {
  (window as any).clearNotificationCache = clearNotificationCache;
  devLog('[clearNotificationCache] Function available globally as window.clearNotificationCache()');
}